import type { LabColor } from './types';

/**
 * Distance perceptuelle CIEDE2000 entre deux couleurs CIE LAB (D65).
 *
 * Implémentation suivant Sharma, Wu, Dalal (2005),
 * "The CIEDE2000 Color-Difference Formula: Implementation Notes,
 * Supplementary Test Data, and Mathematical Observations."
 *
 * Paramètres pondérateurs kL = kC = kH = 1 (usage graphique standard).
 *
 * @returns ΔE2000 ≥ 0. Un JND humain est ≈ 2.3 unités.
 */
export function ciede2000(lab1: LabColor, lab2: LabColor): number {
  const deg = (r: number): number => (r * 180) / Math.PI;
  const rad = (d: number): number => (d * Math.PI) / 180;
  const pow7 = (x: number): number => x * x * x * x * x * x * x;
  const TWENTY_FIVE_POW_7 = pow7(25);

  // Étape 1 : C', a' avec correction G pour chromas faibles.
  const C1 = Math.hypot(lab1.a, lab1.b);
  const C2 = Math.hypot(lab2.a, lab2.b);
  const cBar = (C1 + C2) / 2;
  const cBar7 = pow7(cBar);
  const G = 0.5 * (1 - Math.sqrt(cBar7 / (cBar7 + TWENTY_FIVE_POW_7)));

  const a1p = (1 + G) * lab1.a;
  const a2p = (1 + G) * lab2.a;
  const C1p = Math.hypot(a1p, lab1.b);
  const C2p = Math.hypot(a2p, lab2.b);

  // h' (teinte) en degrés [0, 360). Convention : 0 si a'=b'=0.
  const hueDegrees = (a: number, b: number): number => {
    if (a === 0 && b === 0) return 0;
    const h = deg(Math.atan2(b, a));
    return h < 0 ? h + 360 : h;
  };
  const h1p = hueDegrees(a1p, lab1.b);
  const h2p = hueDegrees(a2p, lab2.b);

  // Étape 2 : différences ΔL', ΔC', ΔH'.
  const dLp = lab2.l - lab1.l;
  const dCp = C2p - C1p;

  // Δh' : 0 si l'une des couleurs est neutre (C1p ou C2p = 0),
  // sinon plus court arc sur le cercle des teintes.
  let dhp: number;
  if (C1p * C2p === 0) {
    dhp = 0;
  } else {
    const diff = h2p - h1p;
    if (Math.abs(diff) <= 180) {
      dhp = diff;
    } else if (diff > 180) {
      dhp = diff - 360;
    } else {
      dhp = diff + 360;
    }
  }
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(rad(dhp) / 2);

  // Étape 3 : moyennes L̄', C̄', H̄'.
  const lpBar = (lab1.l + lab2.l) / 2;
  const cpBar = (C1p + C2p) / 2;

  // H̄' : moyenne circulaire des teintes.
  let hpBar: number;
  if (C1p * C2p === 0) {
    hpBar = h1p + h2p;
  } else if (Math.abs(h1p - h2p) <= 180) {
    hpBar = (h1p + h2p) / 2;
  } else if (h1p + h2p < 360) {
    hpBar = (h1p + h2p + 360) / 2;
  } else {
    hpBar = (h1p + h2p - 360) / 2;
  }

  // Étape 4 : facteurs T, Δθ, R_C, S_L, S_C, S_H, R_T.
  const T =
    1 -
    0.17 * Math.cos(rad(hpBar - 30)) +
    0.24 * Math.cos(rad(2 * hpBar)) +
    0.32 * Math.cos(rad(3 * hpBar + 6)) -
    0.2 * Math.cos(rad(4 * hpBar - 63));

  const dTheta = 30 * Math.exp(-Math.pow((hpBar - 275) / 25, 2));
  const cpBar7 = pow7(cpBar);
  const RC = 2 * Math.sqrt(cpBar7 / (cpBar7 + TWENTY_FIVE_POW_7));

  const lpBarMinus50Sq = (lpBar - 50) * (lpBar - 50);
  const SL = 1 + (0.015 * lpBarMinus50Sq) / Math.sqrt(20 + lpBarMinus50Sq);
  const SC = 1 + 0.045 * cpBar;
  const SH = 1 + 0.015 * cpBar * T;
  const RT = -Math.sin(rad(2 * dTheta)) * RC;

  // Étape 5 : ΔE00 final (kL = kC = kH = 1).
  const termL = dLp / SL;
  const termC = dCp / SC;
  const termH = dHp / SH;

  return Math.sqrt(termL * termL + termC * termC + termH * termH + RT * termC * termH);
}
